import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
} from "@exhousing/design-system";
import { Plus, Receipt, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import { useCreateInvoice, useInvoices } from "@/hooks/api/useInvoices";
import { money } from "@/lib/money";

type DraftLine = { description: string; unit_amount: string; vat_rate: string };

const EMPTY_LINE: DraftLine = { description: "", unit_amount: "", vat_rate: "0" };

/** Issued/partially-paid invoices past their due date are effectively overdue,
 *  even if the backend status hasn't been swept to "overdue" yet. */
function isInvoiceOverdue(inv: {
  status: string;
  due_date: string | null;
  amount_due: number;
}): boolean {
  if (inv.status === "overdue") return true;
  if (inv.status !== "issued" && inv.status !== "partially_paid") return false;
  if (inv.amount_due <= 0 || inv.due_date == null) return false;
  return new Date(inv.due_date) < new Date(new Date().toDateString());
}

type Filter = "all" | "outstanding" | "overdue";

export function InvoicesPage() {
  const { data: invoices, isLoading } = useInvoices();
  const create = useCreateInvoice();
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [lines, setLines] = useState<DraftLine[]>([{ ...EMPTY_LINE }]);

  const rows = (invoices ?? []).filter((inv) => {
    if (filter === "overdue")
      return inv.status === "overdue" || isInvoiceOverdue(inv);
    if (filter === "outstanding")
      return inv.status !== "draft" && inv.amount_due > 0;
    return true;
  });
  const arrearsCount = (invoices ?? []).filter(
    (inv) => inv.status === "overdue" || isInvoiceOverdue(inv),
  ).length;

  const setLine = (i: number, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const total = lines.reduce((sum, l) => {
    const net = Number(l.unit_amount || 0);
    return sum + net + net * Number(l.vat_rate || 0);
  }, 0);

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = lines
      .filter((l) => l.description && l.unit_amount)
      .map((l) => ({
        description: l.description,
        unit_amount: Number(l.unit_amount),
        quantity: 1,
        vat_rate: Number(l.vat_rate || 0),
      }));
    if (!payload.length) {
      toast.error("Add at least one line with a description and amount");
      return;
    }
    create.mutate(
      { currency: "EUR", lines: payload },
      {
        onSuccess: () => {
          toast.success("Draft invoice created");
          setShowForm(false);
          setLines([{ ...EMPTY_LINE }]);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Invoices</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {invoices ? `${invoices.length} invoices` : "Loading…"}
            {arrearsCount > 0 && (
              <>
                {" · "}
                <span className="font-medium text-danger">
                  {arrearsCount} overdue
                </span>
              </>
            )}
          </p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)}>
          <Plus className="size-4" />
          New invoice
        </Button>
      </header>

      <div className="inline-flex rounded-md border border-border p-0.5 text-sm">
        {(
          [
            ["all", "All"],
            ["outstanding", "Outstanding"],
            ["overdue", "Overdue"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setFilter(value)}
            className={
              filter === value
                ? "rounded px-3 py-1 font-medium bg-secondary"
                : "rounded px-3 py-1 text-muted-foreground hover:text-foreground"
            }
          >
            {label}
            {value === "overdue" && arrearsCount > 0 ? ` (${arrearsCount})` : ""}
          </button>
        ))}
      </div>

      {showForm && (
        <Card className="animate-fade-in-up">
          <CardContent className="space-y-4 pt-6">
            <form onSubmit={onCreate} className="space-y-4">
              <div className="space-y-3">
                {lines.map((line, i) => (
                  <div key={i} className="grid grid-cols-12 items-end gap-3">
                    <div className="col-span-6 space-y-1.5">
                      {i === 0 && <Label>Description</Label>}
                      <Input
                        value={line.description}
                        onChange={(e) => setLine(i, { description: e.target.value })}
                        placeholder="e.g. Monthly rent"
                      />
                    </div>
                    <div className="col-span-3 space-y-1.5">
                      {i === 0 && <Label>Amount (excl. VAT)</Label>}
                      <Input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        value={line.unit_amount}
                        onChange={(e) => setLine(i, { unit_amount: e.target.value })}
                        placeholder="0.00"
                        className="text-right tabular-nums"
                      />
                    </div>
                    <div className="col-span-2 space-y-1.5">
                      {i === 0 && <Label>VAT</Label>}
                      <select
                        value={line.vat_rate}
                        onChange={(e) => setLine(i, { vat_rate: e.target.value })}
                        className="flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="0">0%</option>
                        <option value="0.09">9%</option>
                        <option value="0.21">21%</option>
                      </select>
                    </div>
                    <div className="col-span-1 pb-1">
                      {lines.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                          className="text-muted-foreground hover:text-destructive"
                          aria-label="Remove line"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between border-t pt-4">
                <button
                  type="button"
                  onClick={() => setLines((ls) => [...ls, { ...EMPTY_LINE }])}
                  className="text-sm font-medium text-accent hover:underline"
                >
                  + Add line
                </button>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    Total incl. VAT:{" "}
                    <span className="font-semibold tabular-nums text-foreground">
                      {money(total)}
                    </span>
                  </span>
                  <Button type="submit" disabled={create.isPending}>
                    {create.isPending ? "Creating…" : "Create draft"}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-3 font-medium">Number</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Issued</th>
                <th className="px-5 py-3 font-medium">Due</th>
                <th className="px-5 py-3 text-right font-medium">Total</th>
                <th className="px-5 py-3 text-right font-medium">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => {
                const overdue = isInvoiceOverdue(inv);
                return (
                  <tr
                    key={inv.id}
                    className="border-b last:border-0 transition-colors hover:bg-muted/40"
                  >
                    <td className="px-5 py-3">
                      <Link
                        to={`/invoices/${inv.id}`}
                        className="font-medium text-foreground hover:text-accent"
                      >
                        {inv.number ?? "Draft"}
                      </Link>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={inv.status} />
                        {overdue && inv.status !== "overdue" && (
                          <Badge tone="danger" dot>
                            Overdue
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-muted-foreground">
                      {inv.issue_date ?? "—"}
                    </td>
                    <td
                      className={`px-5 py-3 ${
                        overdue ? "font-medium text-danger" : "text-muted-foreground"
                      }`}
                    >
                      {inv.due_date ?? "—"}
                    </td>
                    <td className="px-5 py-3 text-right font-medium tabular-nums">
                      {money(inv.total, inv.currency)}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {inv.amount_due > 0 ? (
                        <span
                          className={
                            overdue ? "font-semibold text-danger" : "text-foreground"
                          }
                        >
                          {money(inv.amount_due, inv.currency)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-muted-foreground">
                    <Receipt className="mx-auto mb-2 size-6 opacity-50" />
                    {filter === "all"
                      ? "No invoices yet."
                      : filter === "overdue"
                        ? "No overdue invoices."
                        : "Nothing outstanding."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
